import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface ContentFilterRule {
  id: string;
  name: string;
  type: 'SPAM' | 'PROFANITY' | 'LINK_LIMIT' | 'CAPS_LOCK' | 'DUPLICATE' | 'SIMILARITY';
  enabled: boolean;
  config: {
    threshold?: number;
    maxLinks?: number;
    maxCapsPercent?: number;
    blacklist?: string[];
    whitelist?: string[];
    similarityThreshold?: number;
    timeWindowHours?: number;
  };
  action: 'FLAG' | 'AUTO_HIDE' | 'AUTO_DELETE' | 'REQUIRE_REVIEW';
  scope: 'POST' | 'COMMENT' | 'MESSAGE' | 'ALL';
  priority: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ContentCheckResult {
  passed: boolean;
  violations: Array<{
    ruleId: string;
    ruleName: string;
    type: string;
    action: string;
    message: string;
  }>;
  recommendedAction: 'ALLOW' | 'FLAG' | 'HIDE' | 'DELETE' | 'REVIEW';
}

export interface CreateRuleData {
  name: string;
  type: 'SPAM' | 'PROFANITY' | 'LINK_LIMIT' | 'CAPS_LOCK' | 'DUPLICATE' | 'SIMILARITY';
  enabled: boolean;
  config: Record<string, any>;
  action: 'FLAG' | 'AUTO_HIDE' | 'AUTO_DELETE' | 'REQUIRE_REVIEW';
  scope: 'POST' | 'COMMENT' | 'MESSAGE' | 'ALL';
  priority: number;
}

export interface UpdateRuleData extends Partial<CreateRuleData> {}

const contentFilterService = {
  // Get all filter rules
  getAll: async (): Promise<ContentFilterRule[]> => {
    const response = await axios.get(`${API_URL}/api/admin/content-filters`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    // Backend returns { success, rules, pagination }
    return response.data.rules || [];
  },

  // Get single rule
  getById: async (id: string): Promise<ContentFilterRule> => {
    const response = await axios.get(`${API_URL}/api/admin/content-filters/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    // Backend returns { success, rule }
    return response.data.rule;
  },

  // Create new rule
  create: async (data: CreateRuleData): Promise<ContentFilterRule> => {
    const response = await axios.post(`${API_URL}/api/admin/content-filters`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    // Backend returns { success, rule }
    return response.data.rule;
  },

  // Update existing rule
  update: async (id: string, data: UpdateRuleData): Promise<ContentFilterRule> => {
    const response = await axios.patch(`${API_URL}/api/admin/content-filters/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    // Backend returns { success, rule }
    return response.data.rule;
  },

  // Delete rule
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/admin/content-filters/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },

  // Toggle rule enabled status
  toggle: async (id: string): Promise<ContentFilterRule> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/content-filters/${id}/toggle`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    // Backend returns { success, rule }
    return response.data.rule;
  },

  // Test content against rules
  checkContent: async (content: string, type: 'POST' | 'COMMENT' | 'MESSAGE'): Promise<ContentCheckResult> => {
    const response = await axios.post(
      `${API_URL}/api/admin/content-filters/check`,
      // Backend expects { content, contentType } and derives userId from JWT
      { content, contentType: type },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    // Backend returns { success, results }
    return response.data.results;
  },
};

export default contentFilterService;
